/**
 * <venner-preorder>
 *
 * Avisos de preventa para las variantes agotadas que siguen a la venta.
 *
 * Este elemento vive DENTRO de <buy-buttons>, que es de los que Prestige reemplaza con HTML recién pedido
 * al servidor en cada cambio de variante. Eso significa que el aviso, la confirmación de envío express y
 * las propiedades que viajan al carrito llegan siempre resueltas en Liquid: no hay que escuchar ningún
 * evento ni encender y apagar nada a mano, y por tanto no hay forma de que se queden desincronizadas.
 *
 * Lo único que queda para el navegador es lo que está fuera de <buy-buttons> y el servidor no puede tocar
 * desde aquí: la marca en los swatches, la etiqueta sobre la imagen y las etiquetas de arriba del título.
 * Todo eso se aplica en `apply()`, que es idempotente y cubre los dos sentidos (poner y quitar), de modo
 * que da igual en qué orden se conecten y desconecten los elementos al repintar.
 */
if (!window.customElements.get('venner-preorder')) {
  class VennerPreorder extends HTMLElement {
    connectedCallback() {
      this.config = this.readConfig();

      if (!this.config) {
        return;
      }

      this.optionValueIds = new Set(this.config.optionValueIds || []);
      this.optionValueNames = new Set(this.config.optionValueNames || []);
      this.hiddenBadges = this.config.hiddenBadges || [];

      this.apply(this.hasAttribute('data-preorder'));
    }

    readConfig() {
      const script = this.querySelector('[data-venner-preorder-config]');

      if (!script) {
        return null;
      }

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
    }

    /**
     * En el quick buy este elemento vive dentro de un modal, así que la galería y las etiquetas del título
     * solo se tocan en la página de producto real.
     */
    get isMainProduct() {
      return this.getAttribute('context') === 'main_product';
    }

    /**
     * Todo lo que se toca vive dentro del mismo <product-rerender> que Prestige repinta. Acotar ahí evita
     * pisar otras secciones, y en el quick buy acota al contenido del modal.
     */
    get root() {
      return this.closest('product-rerender') || this.closest('.shopify-section') || document;
    }

    apply(isPreorder) {
      this.decorateOptionValues();

      if (this.isMainProduct) {
        this.renderMediaBadge(isPreorder);
        this.renderFeatureBadges(isPreorder);
      }
    }

    /**
     * Marca los colores en preventa en el selector de variantes. Cubre las formas en que Prestige los puede
     * dibujar: swatch redondo, miniatura, bloque de texto y desplegable.
     */
    decorateOptionValues() {
      if (this.optionValueIds.size === 0 && this.optionValueNames.size === 0) {
        return;
      }

      const label = this.config.swatchLabel || '';

      this.root.querySelectorAll('input[data-option-position]').forEach((input) => {
        if (!this.optionValueIds.has(String(input.value))) {
          return;
        }

        this.decorateControl(this.labelFor(input), label);

        // El nombre del color elegido («Color: Negro») también lleva la marca, porque en el desplegable el
        // cliente ya no ve la lista una vez elegido.
        if (input.checked && label) {
          const selectedValue = input
            .closest('.variant-picker__option')
            ?.querySelector('.variant-picker__option-info .h-stack > span:last-child');

          if (selectedValue && !selectedValue.querySelector('.venner-preorder-tag')) {
            selectedValue.append(this.buildTag(label));
          }
        }
      });

      // Cuando cada color es un producto aparte, Prestige dibuja los colores no seleccionados como enlaces
      // y sin input, así que ahí no hay ID que comparar: se reconocen por su nombre.
      if (this.optionValueNames.size === 0) {
        return;
      }

      this.root
        .querySelectorAll('a.color-swatch, a.thumbnail-swatch, a.block-swatch, .popover__value-option > a')
        .forEach((anchor) => {
          const text = (anchor.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
          const matches = Array.from(this.optionValueNames).some(
            (name) => name && (text === name || text.startsWith(`${name} `))
          );

          if (matches) {
            this.decorateControl(anchor, label);
          }
        });
    }

    /**
     * Los swatches redondos no tienen sitio para texto dentro: llevan la etiqueta colgada con CSS. Los
     * selectores de texto sí la admiten dentro.
     */
    decorateControl(control, label) {
      if (!control) {
        return;
      }

      if (control.classList.contains('color-swatch') || control.classList.contains('thumbnail-swatch')) {
        control.classList.add('venner-preorder-swatch');

        if (label) {
          control.setAttribute('data-venner-preorder-tag', label);
          control.parentElement?.classList.add('venner-preorder-has-tags');
        }
      } else if (label && !control.querySelector('.venner-preorder-tag')) {
        control.append(this.buildTag(label));
      }
    }

    /**
     * En los swatches la etiqueta es hermana del input; en el desplegable el input va dentro de ella.
     */
    labelFor(input) {
      if (input.id) {
        const label = this.root.querySelector(`label[for="${CSS.escape(input.id)}"]`);

        if (label) {
          return label;
        }
      }

      return input.closest('label') || (input.nextElementSibling?.tagName === 'LABEL' ? input.nextElementSibling : null);
    }

    buildTag(text) {
      const tag = document.createElement('span');

      tag.className = 'venner-preorder-tag';
      tag.textContent = text;

      return tag;
    }

    /**
     * Se cuelga de la lista de imágenes y no de la galería entera: con las miniaturas a un lado, la esquina
     * de la galería cae sobre ellas y no sobre la foto.
     */
    renderMediaBadge(isPreorder) {
      const gallery = this.root.querySelector('.product-gallery__image-list');

      if (!gallery) {
        return;
      }

      // Se busca en el DOM en vez de guardarlo en el elemento: al repintar hay dos <venner-preorder> vivos
      // un instante y una referencia propia dejaría la etiqueta anterior colgada.
      const existing = gallery.querySelector(':scope > .venner-preorder-media-badge');
      const text = this.config.mediaBadge;

      if (!isPreorder || !text) {
        existing?.remove();
        gallery.classList.remove('venner-preorder-has-badge');
        return;
      }

      const badge = existing || document.createElement('div');

      badge.className = 'venner-preorder-media-badge';
      badge.textContent = text;
      gallery.classList.add('venner-preorder-has-badge');

      if (!existing) {
        gallery.prepend(badge);
      }
    }

    /**
     * Las etiquetas de arriba del título («Bestseller», «Envío express») son bloques de la sección. En
     * preventa se esconden las que prometen envío inmediato y en su hueco se pone la de preventa: no pueden
     * convivir, porque juntas dicen lo contrario la una de la otra.
     */
    renderFeatureBadges(isPreorder) {
      const items = Array.from(this.root.querySelectorAll('[data-block-type="feature-with-icon"]'));

      if (items.length === 0) {
        return;
      }

      let anchor = null;

      items.forEach((item) => {
        const text = (item.textContent || '').trim().toLowerCase();
        const shouldHide = this.hiddenBadges.some((needle) => needle && text.includes(needle));

        if (!shouldHide) {
          return;
        }

        anchor = anchor || item;
        item.toggleAttribute('hidden', isPreorder);
      });

      const existing = this.root.querySelector('.venner-preorder-feature-badge')?.closest('.product-info__block-item');

      if (!isPreorder || !this.topBadgeTemplate) {
        existing?.remove();
        return;
      }

      if (existing) {
        return;
      }

      const badge = this.topBadgeTemplate.content.firstElementChild.cloneNode(true);
      const reference = anchor || items[items.length - 1];

      // Va donde estaba la etiqueta de envío express; si el producto no tiene ninguna, detrás de la última.
      reference.parentElement.insertBefore(badge, anchor || reference.nextSibling);
    }

    get topBadgeTemplate() {
      return this.querySelector('[data-venner-preorder-top-badge]');
    }
  }

  window.customElements.define('venner-preorder', VennerPreorder);
}
